import {
    QueryConstraint,
    QueryDocumentSnapshot,
    QuerySnapshot,
    DocumentData,
    query,
    limit,
    getDocs,
    where,
    startAfter,
    orderBy,
} from 'firebase/firestore'
import { ordersRef } from '../modules/firebase'
import {
    getQueryType,
    getWhereQuery,
    QueryType,
    getFromCachedAPI,
} from '../modules/util'
import { Beehive, BookOrder } from '../types'
import { decodeRef } from '@LITTA-CO/shared/reference'
import {
    isRecurringBatchReference,
    normaliseRecurringBatchReference,
} from '@/lib/recurring-bookings/batch-reference'

export { isRecurringBatchReference }

/**
 * Checks if a string looks like a Firebase document ID
 */
export const isFirebaseId = (text: string): boolean => {
    return text.length >= 20 && text.length <= 30 && /^[A-Za-z0-9]+$/.test(text)
}

/**
 * Checks if a string is a reference (LTA, LTC, LTB, LTP, LTG)
 * or quote reference (LRQ, LPQ, LCQ, LBQ)
 */
export const isReference = (text: string): boolean => {
    return (
        text.startsWith('LTA') ||
        text.startsWith('LTC') ||
        text.startsWith('LTB') ||
        text.startsWith('LTP') ||
        text.startsWith('LTG') ||
        text.startsWith('LRQ') ||
        text.startsWith('LPQ') ||
        text.startsWith('LCQ') ||
        text.startsWith('LBQ')
    )
}

/**
 * Checks if a string is a quote reference (LRQ, LPQ, LCQ, LBQ)
 */
export const isQuoteReference = (text: string): boolean => {
    return (
        text.startsWith('LRQ') ||
        text.startsWith('LPQ') ||
        text.startsWith('LCQ') ||
        text.startsWith('LBQ')
    )
}

/**
 * Checks if query type is email, phone, or postcode
 */
export const isContactQuery = (queryType: QueryType): boolean => {
    return (
        queryType === QueryType.Email ||
        queryType === QueryType.Postcode ||
        queryType === QueryType.PhoneNumber
    )
}

/**
 * External references: stored in `externalReference` (L- and WLWA- formats)
 */
export const isExternalReference = (text: string): boolean => {
    return text.startsWith('L-') || text.startsWith('WLWA-')
}

/**
 * Builds Firebase query constraints for search
 */
export const buildSearchQuery = (
    searchValue: string,
    useLTA: boolean = false,
): QueryConstraint[] | null => {
    const trimmed = searchValue.trim()
    if (!trimmed) return null

    // External references (L-, WLWA-)
    if (isExternalReference(trimmed) && !useLTA) {
        return [where('externalReference', '==', trimmed)]
    }

    if (isExternalReference(trimmed) && useLTA) {
        try {
            const decodedRef = decodeRef(trimmed)
            return [where('LTA', '==', decodedRef)]
        } catch (error) {
            console.error('Error decoding reference:', error)
            return null
        }
    }

    if (isRecurringBatchReference(trimmed)) {
        return [
            where(
                'recurringBatchRef',
                '==',
                normaliseRecurringBatchReference(trimmed),
            ),
        ]
    }

    // Check if it's a quote reference
    if (isQuoteReference(trimmed)) {
        return [where('quote_ref', '==', trimmed)]
    }

    // Check if it's a regular reference or Firebase ID
    if (isReference(trimmed) || isFirebaseId(trimmed)) {
        const queryType = getQueryType(trimmed)
        const queries = getWhereQuery(queryType, trimmed)
        return queries.length > 0 ? queries : null
    }

    // Check if it's email, phone, or postcode
    const queryType = getQueryType(trimmed)
    if (isContactQuery(queryType)) {
        const queries = getWhereQuery(queryType, trimmed)
        return queries.length > 0 ? queries : null
    }

    return null
}

/**
 * Interface for order search results
 */
export interface OrderSearchResult {
    results: BookOrder[]
    lastDoc: QueryDocumentSnapshot<DocumentData> | null
    hasMore: boolean
}

/**
 * Searches all orders matching the search value without pagination limits
 * Handles "L-" external references with fallback to decoded LTA search
 */
export const searchAllOrders = async (
    searchValue: string,
): Promise<BookOrder[]> => {
    try {
        const trimmed = searchValue.trim()
        const allResults: BookOrder[] = []
        let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null
        const pageSize = 100 // Use larger page size for efficiency when loading all

        // Handle external reference (L- prefix) with fallback
        if (isExternalReference(trimmed)) {
            const externalRefConstraints = buildSearchQuery(trimmed, false)

            if (externalRefConstraints) {
                // Keep paginating until no more results
                while (true) {
                    let firebaseQuery

                    if (lastDoc) {
                        firebaseQuery = query(
                            ordersRef,
                            ...externalRefConstraints,
                            startAfter(lastDoc),
                            limit(pageSize),
                        )
                    } else {
                        firebaseQuery = query(
                            ordersRef,
                            ...externalRefConstraints,
                            limit(pageSize),
                        )
                    }

                    const snapshot: QuerySnapshot<DocumentData> =
                        await getDocs(firebaseQuery)

                    if (snapshot.empty) {
                        // If no results and this is the first page, try fallback to decoded LTA
                        if (allResults.length === 0 && !lastDoc) {
                            const ltaConstraints = buildSearchQuery(trimmed, true)

                            if (ltaConstraints) {
                                const fallbackQuery = query(
                                    ordersRef,
                                    ...ltaConstraints,
                                    limit(pageSize),
                                )

                                const fallbackSnapshot: QuerySnapshot<DocumentData> =
                                    await getDocs(fallbackQuery)

                                if (!fallbackSnapshot.empty) {
                                    const fallbackResults = fallbackSnapshot.docs.map(
                                        (doc: QueryDocumentSnapshot<DocumentData>) =>
                                            doc.data() as BookOrder,
                                    )
                                    allResults.push(...fallbackResults)

                                    // Continue paginating if we got a full page
                                    if (fallbackSnapshot.docs.length === pageSize) {
                                        lastDoc =
                                            fallbackSnapshot.docs[
                                                fallbackSnapshot.docs.length - 1
                                            ]
                                        continue
                                    }
                                }
                            }
                        }
                        break
                    }

                    const results = snapshot.docs.map(
                        (doc: QueryDocumentSnapshot<DocumentData>) =>
                            doc.data() as BookOrder,
                    )
                    allResults.push(...results)

                    // If we got fewer than pageSize, we've reached the end
                    if (snapshot.docs.length < pageSize) {
                        break
                    }

                    lastDoc = snapshot.docs[snapshot.docs.length - 1]
                }

                return allResults
            }
        }

        // Standard search logic for other query types
        const constraints = buildSearchQuery(trimmed)

        if (!constraints) {
            return []
        }

        // Keep paginating until no more results
        while (true) {
            let firebaseQuery

            if (lastDoc) {
                firebaseQuery = query(
                    ordersRef,
                    ...constraints,
                    startAfter(lastDoc),
                    limit(pageSize),
                )
            } else {
                firebaseQuery = query(ordersRef, ...constraints, limit(pageSize))
            }

            const snapshot: QuerySnapshot<DocumentData> =
                await getDocs(firebaseQuery)

            if (snapshot.empty) {
                break
            }

            const results = snapshot.docs.map(
                (doc: QueryDocumentSnapshot<DocumentData>) =>
                    doc.data() as BookOrder,
            )
            allResults.push(...results)

            // If we got fewer than pageSize, we've reached the end
            if (snapshot.docs.length < pageSize) {
                break
            }

            lastDoc = snapshot.docs[snapshot.docs.length - 1]
        }

        return allResults
    } catch (error) {
        console.error('Error searching all orders:', error)
        return []
    }
}

/**
 * Searches orders with pagination support
 * Handles "L-" external references with fallback to decoded LTA search
 */
export const searchOrders = async (
    searchValue: string,
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
    pageSize: number = 11,
): Promise<OrderSearchResult> => {
    try {
        const trimmed = searchValue.trim()

        // Handle external reference (L- prefix) with fallback
        if (isExternalReference(trimmed)) {
            // First, try searching by externalReference
            const externalRefConstraints = buildSearchQuery(trimmed, false)

            if (externalRefConstraints) {
                let firebaseQuery

                if (lastDoc) {
                    firebaseQuery = query(
                        ordersRef,
                        ...externalRefConstraints,
                        startAfter(lastDoc),
                        limit(pageSize),
                    )
                } else {
                    firebaseQuery = query(
                        ordersRef,
                        ...externalRefConstraints,
                        limit(pageSize),
                    )
                }

                const snapshot = await getDocs(firebaseQuery)

                // If we found results, return them
                if (!snapshot.empty) {
                    const hasMoreResults = snapshot.docs.length === pageSize
                    const docsToProcess = hasMoreResults
                        ? snapshot.docs.slice(0, pageSize - 1)
                        : snapshot.docs
                    const newLastDoc =
                        docsToProcess.length > 0
                            ? docsToProcess[docsToProcess.length - 1]
                            : null
                    const results = docsToProcess.map(
                        doc => doc.data() as BookOrder,
                    )
                    return {
                        results,
                        lastDoc: newLastDoc,
                        hasMore: hasMoreResults,
                    }
                }

                // If no results and this is the first page (no lastDoc), try fallback to decoded LTA
                if (!lastDoc) {
                    const ltaConstraints = buildSearchQuery(trimmed, true)

                    if (ltaConstraints) {
                        const fallbackQuery = query(
                            ordersRef,
                            ...ltaConstraints,
                            limit(pageSize),
                        )

                        const fallbackSnapshot = await getDocs(fallbackQuery)

                        if (!fallbackSnapshot.empty) {
                            const hasMoreResults =
                                fallbackSnapshot.docs.length === pageSize
                            const docsToProcess = hasMoreResults
                                ? fallbackSnapshot.docs.slice(0, pageSize - 1)
                                : fallbackSnapshot.docs
                            const newLastDoc =
                                docsToProcess.length > 0
                                    ? docsToProcess[docsToProcess.length - 1]
                                    : null
                            const results = docsToProcess.map(
                                doc => doc.data() as BookOrder,
                            )
                            return {
                                results,
                                lastDoc: newLastDoc,
                                hasMore: hasMoreResults,
                            }
                        }
                    }
                }

                // No results from either search
                return { results: [], lastDoc: null, hasMore: false }
            }
        }

        // Standard search logic for other query types
        const constraints = buildSearchQuery(trimmed)

        if (!constraints) {
            return { results: [], lastDoc: null, hasMore: false }
        }

        let firebaseQuery

        if (lastDoc) {
            firebaseQuery = query(
                ordersRef,
                ...constraints,
                startAfter(lastDoc),
                limit(pageSize),
            )
        } else {
            firebaseQuery = query(ordersRef, ...constraints, limit(pageSize))
        }

        const snapshot = await getDocs(firebaseQuery)

        if (snapshot.empty) {
            return { results: [], lastDoc: null, hasMore: false }
        }

        // Check if there are more results (we requested pageSize, got pageSize means there might be more)
        const hasMoreResults = snapshot.docs.length === pageSize
        const docsToProcess = hasMoreResults
            ? snapshot.docs.slice(0, pageSize - 1)
            : snapshot.docs
        const newLastDoc =
            docsToProcess.length > 0
                ? docsToProcess[docsToProcess.length - 1]
                : null

        const results = docsToProcess.map(doc => doc.data() as BookOrder)

        return { results, lastDoc: newLastDoc, hasMore: hasMoreResults }
    } catch (error) {
        console.error('Error searching orders:', error)
        return { results: [], lastDoc: null, hasMore: false }
    }
}

/**
 * Interface for beehive page search result
 */
export interface BeehivePageSearchResult {
    title: string
    subtitle?: string
    type: 'page'
    navigationOptions: Array<{ label: string; route: string }>
}

/**
 * Searches beehive pages
 */
export const searchBeehivePages = async (
    query: string,
): Promise<BeehivePageSearchResult[]> => {
    try {
        const pages = await getFromCachedAPI<Beehive>(
            'beehive',
            'active,==,true',
        )
        if (!pages || pages.length === 0) return []

        const filtered = pages.filter(
            page =>
                page.title.toLowerCase().includes(query.toLowerCase()) ||
                page.url.toLowerCase().includes(query.toLowerCase()),
        )

        return filtered.map(page => ({
            title: page.title,
            subtitle: page.url,
            type: 'page' as const,
            navigationOptions: [
                {
                    label: 'Go to page',
                    route: page.url,
                },
            ],
        }))
    } catch (error) {
        console.error('Error searching beehive pages:', error)
        return []
    }
}
